import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import googleDriveLogo from "@/assets/google-drive-logo.png";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import googleMeetLogo from "@/assets/google-meet-logo.png";
import swiftaidLogo from "@/assets/swiftaid-logo.png";

const availableIntegrations = [
  {
    name: "Google Drive",
    logo: googleDriveLogo,
    description: "Store and manage PTA documents securely in the cloud. Share files with committee members, collaborate on documents in real-time, and maintain organized folder structures for meeting minutes, policies, financial records, and event planning materials. Keep your PTA's documentation accessible and well-organized.",
    status: "available",
    iconClass: "h-12 w-14"
  },
  {
    name: "Google Calendar",
    logo: googleCalendarLogo,
    description: "Seamlessly sync PTA events and meetings with Google Calendar to keep everyone informed. Automatically create calendar invites for meetings, share event schedules with members and parents, send reminders for important dates, and ensure no one misses crucial PTA activities or deadlines.",
    status: "available",
    planRequired: "Standard"
  },
  {
    name: "Google Meet",
    logo: googleMeetLogo,
    description: "Create Google Meet video conferences directly from PTA Hub when scheduling meetings. Enable remote collaboration, conduct online PTA meetings, host virtual parent consultations, and provide flexible meeting options for busy parents. Available as part of the Google Calendar integration.",
    status: "available",
    planRequired: "Standard"
  }
];

const plannedIntegrations = [
  {
    name: "Swiftaid",
    logo: swiftaidLogo,
    description: "We plan to integrate with Swiftaid to allow managing Gift Aid for your PTA much easier. Automate Gift Aid claims, track donor declarations, maximize your fundraising income, and simplify the administrative burden of claiming Gift Aid on eligible donations.",
    status: "planned"
  }
];

const Integrations = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <section className="py-20 bg-gradient-to-b from-background to-muted/20">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Platform Integrations
              </h1>
              <p className="text-xl text-muted-foreground">
                Connect PTA Hub with the tools you already use to streamline your workflow and enhance collaboration.
              </p>
            </div>

            {/* Available Integrations */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <h2 className="text-3xl font-bold">Available Integrations</h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availableIntegrations.map((integration) => (
                  <Card key={integration.name} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-2">
                        {integration.logo && (
                          <img 
                            src={integration.logo} 
                            alt={`${integration.name} logo`} 
                            className={integration.iconClass || "w-12 h-12"}
                          />
                        )}
                        <div>
                          <CardTitle className="text-xl">{integration.name}</CardTitle>
                          {integration.planRequired && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {integration.planRequired} Plan
                            </Badge>
                          )}
                        </div>
                      </div>
                      <CardDescription className="text-base leading-relaxed">
                        {integration.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>

            {/* Planned Integrations */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <h2 className="text-3xl font-bold">Planned Integrations</h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plannedIntegrations.map((integration) => (
                  <Card key={integration.name} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-2">
                        {integration.logo ? (
                          <img 
                            src={integration.logo} 
                            alt={`${integration.name} logo`} 
                            className="h-10 w-auto"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                            <span className="text-lg font-bold text-muted-foreground">
                              {integration.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <CardDescription className="text-base leading-relaxed">
                        {integration.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
    </div>
  );
};

export default Integrations;
